/**
 * Performance utilities for frontend optimization
 * - Lazy loading with preloading
 * - Bundle splitting strategies
 * - Request deduplication
 */

import React, { lazy, ReactNode } from 'react';

/**
 * Preload a lazy component on demand
 * Useful for preloading heavy components before user navigation
 */
export function preloadComponent(
  importFunc: () => Promise<{ default: React.ComponentType<any> }>
): void {
  importFunc();
}

/**
 * Create a lazily loaded component with preload capability
 * Usage: const Reports = lazyWithPreload(() => import('@/pages/Reports'));
 */
export function lazyWithPreload<P>(
  importFunc: () => Promise<{ default: React.ComponentType<P> }>
): React.LazyExoticComponent<React.ComponentType<P>> & {
  preload: () => Promise<{ default: React.ComponentType<P> }>;
} {
  const Component = lazy(importFunc);
  (Component as any).preload = importFunc;
  return Component as any;
}

/**
 * Hook to preload components on mouse over (for links)
 * Usage: const reportProps = usePreloadOnHover(() => import('@/pages/Reports'))
 */
export function createPreloadProps(
  importFunc: () => Promise<{ default: React.ComponentType<any> }>
) {
  return {
    onMouseEnter: () => preloadComponent(importFunc),
    onFocus: () => preloadComponent(importFunc),
  };
}

/**
 * Request deduplication wrapper
 * Prevents multiple identical requests from being sent simultaneously
 */
const pendingRequests = new Map<string, Promise<any>>();

export function memoizedFetch<T>(
  key: string,
  fetchFn: () => Promise<T>
): Promise<T> {
  if (pendingRequests.has(key)) {
    return pendingRequests.get(key) as Promise<T>;
  }

  const promise = fetchFn()
    .then((result) => {
      setTimeout(() => pendingRequests.delete(key), 100);
      return result;
    })
    .catch((error) => {
      pendingRequests.delete(key);
      throw error;
    });

  pendingRequests.set(key, promise);
  return promise;
}

/**
 * Debounce hook for search/filter operations
 */
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = React.useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

export class LazyLoadingErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Lazy loading error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-2">Erro ao carregar</h1>
            <p className="text-gray-600 mb-4">Desculpe, houve um erro ao carregar esta página.</p>
            <button
              onClick={() => {
                this.setState({ hasError: false });
                window.location.reload();
              }}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Preload critical pages after initial load
 */
export function preloadCriticalPages(): void {
  setTimeout(() => {
    preloadComponent(() => import('@/pages/Dashboard'));
  }, 1000);

  setTimeout(() => {
    preloadComponent(() => import('@/pages/Tickets'));
  }, 2000);

  setTimeout(() => {
    preloadComponent(() => import('@/pages/Contacts'));
  }, 3000);
}

/**
 * Image optimization helper
 * Lazy load images using Intersection Observer
 */
export function useImageLazyLoading(
  ref: React.RefObject<HTMLImageElement>
): void {
  React.useEffect(() => {
    if (!ref.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            observer.unobserve(img);
          }
        }
      },
      { rootMargin: '50px' }
    );

    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [ref]);
}
